/* @flow */

/*
  Visually indicate how close the user is to free shipping.
*/

import type { ShippingData } from "shop-state/types";

import React from "react";
import styles from "./styles.scss";
import cn from "classnames";
import { useTranslate } from "@awardit/react-use-translate";
import useFormat from "helpers/use-format";

type Props = {
  className?: string,
  shippingData: ?ShippingData,
};

const FreeShippingMeter = ({ shippingData, className }: Props): React$Node => {
  const t = useTranslate();
  const { formatPrice } = useFormat();

  if (!shippingData) {
    return null;
  }

  const {
    freeShippingAmountNeeded,
    freeShippingThreshold,
    isEligibleForFreeShipping,
  } = shippingData;

  if (
    (freeShippingAmountNeeded === null || freeShippingAmountNeeded === undefined) ||
    (freeShippingThreshold === null || freeShippingThreshold === undefined)
  ) {
    return null;
  }

  const currentValue = freeShippingThreshold - freeShippingAmountNeeded;

  return (
    <div className={cn(className, styles.block)}>
      <div className={styles.bar}>
        <div
          className={styles.progress}
          role="progressbar"
          aria-valuenow={currentValue}
          aria-valuemin={0}
          aria-valuemax={freeShippingThreshold}
          aria-labelledby="progress-label"
          style={{
            transform: `translateX(${Math.min(currentValue / freeShippingThreshold * 100, 100)}%)`,
          }}
        />
      </div>

      <p id="progress-label" className={styles.text}>
        {isEligibleForFreeShipping ?
          t("CART.FREE_SHIPPING") :
          t("CART.FREE_SHIPPING_AT", { left: formatPrice(freeShippingAmountNeeded) })
        }
      </p>
    </div>
  );
};

export default FreeShippingMeter;
